/**
 * Sign-in and token acquisition. Real builds use MSAL against btntl. Test builds (VITE_AUTH_MODE=mock)
 * skip MSAL so Playwright and accessibility checks can run against a mocked API; mock mode refuses to
 * run anywhere but localhost, so it can never serve a deployed environment.
 */
import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser';
import { createContext, useContext, type ReactNode } from 'react';
import { apiScope } from './config';

export interface Auth {
  signedIn: boolean;
  userName: string | null;
  getToken(): Promise<string>;
  signIn(): void;
  signOut(): void;
}

const AuthContext = createContext<Auth | null>(null);

export function useAuth(): Auth {
  const a = useContext(AuthContext);
  if (!a) throw new Error('AuthProvider missing');
  return a;
}

export function isMockMode(): boolean {
  return import.meta.env.VITE_AUTH_MODE === 'mock';
}

export function mockAllowed(): boolean {
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

export function MockAuthProvider({ children }: { children: ReactNode }) {
  const auth: Auth = { signedIn: true, userName: 'Test User', getToken: async () => 'mock-token', signIn: () => undefined, signOut: () => undefined };
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function MsalAuthProvider({ msal, children }: { msal: IPublicClientApplication; children: ReactNode }) {
  const account = msal.getActiveAccount();
  const auth: Auth = {
    signedIn: Boolean(account),
    userName: account?.name ?? null,
    async getToken() {
      const request = { scopes: [apiScope()], account: msal.getActiveAccount() ?? undefined };
      try {
        return (await msal.acquireTokenSilent(request)).accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) await msal.acquireTokenRedirect(request);
        throw e;
      }
    },
    signIn: () => void msal.loginRedirect({ scopes: [apiScope()] }),
    signOut: () => void msal.logoutRedirect(),
  };
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
